import {
  CampaignType,
  FallbackImage,
  MOBILE_SCREEN_SIZE,
  ProductRewardSearchResult,
  RetailerIconGroup,
  RewardPillWithText,
  TABLET_SCREEN_SIZE,
  decodeHTMLCharacters,
  formatMoney,
  getAppConsistentParams,
  getEntityImage,
  getProductUrl,
  getPurchaseRewardAmountForProduct,
  isServerSideCheckoutEligible,
} from "@brandclub/common-ui";
import { BaseComponentPropsType } from "@brandclub/common-ui/BrandclubLanding/type";
import { styled, useMediaQuery, useTheme } from "@mui/material";
import _uniq from "lodash/uniq";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { addOptimisticShoppingCartItem } from "@/redux/reducers/checkout";
import { useCheckoutDrawer } from "@/components/Checkout/useCheckoutDrawer";
import { getAllCarts } from "@/redux/selectors";
import {
  SolidButton,
  OutlineButton,
} from "@/components/StoreComponents/StoreButton";
import { CheckCircleIcon } from "lucide-react";

const ProductCard = styled("div")(({ theme }) => ({
  border: "1px solid #dcdddc",
  width: "100%",
  height: "100%",
  borderRadius: 12,
  padding: "16px 16px 16px 22px",
  boxSizing: "border-box",
  transition: "background-color 0.4s ease-in-out",
  "&:hover": {
    backgroundColor: "#F6F8FA",
  },
  "*": {
    mixBlendMode: "multiply",
  },

  [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    padding: 10,
    width: "100%",
  },
  ".image": {
    width: "calc(100% - 24px)",
    maxWidth: "190px",
    aspectRatio: "1/1",
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 auto 0",
    img: {
      maxHeight: "100%",
      maxWidth: "100%",
      aspectRatio: "1",
      objectFit: "contain",
    },
  },
  ".title": {
    textDecoration: "none",
    color: theme.vars.palette.text.primary,
    fontSize: 14,
    fontWeight: 500,
    marginTop: 20,
    width: "100%",
    overflow: "hidden",
    letterSpacing: 0.36,
    WebkitLineClamp: 3,
    lineHeight: 1.5,
    height: `4.5em`, // line height * 2
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    wordBreak: "break-word",
    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
  ".price": {
    color: theme.vars.palette.text.primary,
    fontSize: 20,
    fontWeight: 600,
    marginTop: 10,
    marginBottom: 10,
    display: "flex",
    columnGap: 12,
    alignItems: "center",

    [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 16,
    },
    ".value": {
      display: "flex",
      alignItems: "center",
    },
  },
}));

export const ConciergeProductCard = ({
  product,
  onShareClick,
}: {
  product: ProductRewardSearchResult;
  onShareClick?: BaseComponentPropsType["onShareClick"];
} & {}) => {
  const dispatch = useAppDispatch();
  const { openDrawer } = useCheckoutDrawer();
  const allCarts = useAppSelector(getAllCarts);
  const {
    affiliateRewardAmount,
    hasActiveInstantReward,
    matchedProducts,
    checkoutEligibility,
    retailerId,
    retailPrice,
    stacklineSku,
    title,
    brandId,
    retailerSku,
    retailerProductPageUrl,
  } = product;

  const img = getEntityImage(stacklineSku, "product");
  const cleanProductTitle = decodeHTMLCharacters(title || "");

  const purchaseRewardAmount = getPurchaseRewardAmountForProduct(product);
  const rewardPercent =
    !!retailPrice && !!purchaseRewardAmount
      ? (purchaseRewardAmount * 100) / retailPrice
      : 0;

  const matchedProductsRetailerIds = (matchedProducts ?? []).map(
    (matchedProduct) => matchedProduct.retailerId
  );
  const retailerIds = _uniq([retailerId, ...matchedProductsRetailerIds]);

  const theme = useTheme();
  const smBreakpoint = theme.breakpoints.values.sm;
  const tabletView = useMediaQuery(`(min-width: ${smBreakpoint}px)`);
  const mobileView = !tabletView;

  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = getProductUrl({ title, stacklineSku, urlSearchParams });

  const retailerLogoWidth = mobileView ? 15 : 25;

  // Check if product is already in cart
  const isInCart = allCarts.some((cart) =>
    cart.items?.some((item) => item.stacklineSku === stacklineSku)
  );

  const handleAddToBag = async () => {
    if (isServerSideCheckoutEligible(checkoutEligibility)) {
      try {
        const cartItem = {
          stacklineSku,
          retailerId,
          retailerSku: retailerSku || "",
          retailPrice: retailPrice || 0,
          brandId: brandId || 0,
          retailerParentSku: retailerSku || "",
          retailerProductPageUrl: retailerProductPageUrl || "",
        };

        dispatch(
          addOptimisticShoppingCartItem({
            items: [
              {
                ...cartItem,
                quantity: 1,
              },
            ],
            retailerId,
            totalItems: 1,
          })
        );
        openDrawer();
      } catch (error) {
        console.error("Error adding to bag:", error);
      }
    }
  };
  return (
    <ProductCard>
      <RewardPillWithText
        onPillClick={onShareClick}
        rewardAmount={affiliateRewardAmount ?? 0}
        rewardCampaignType={CampaignType.AffiliateRewardCampaign}
        containerStyle={{ justifyContent: "end" }}
        baseFontSize={mobileView ? 11 : 14}
      />
      <a href={targetUrl} target="_blank" rel="noreferrer" className="image">
        <FallbackImage
          src={img}
          fallbackImage={img.replace(
            "media.brandclub.com",
            "media-api.brandclub.com"
          )}
          alt={title}
        />
      </a>
      <a
        href={targetUrl}
        target="_blank"
        style={{ textDecoration: "none" }}
        rel="noreferrer"
        className="title"
      >
        {cleanProductTitle}
      </a>
      <div className="price">
        <div className="value">{formatMoney(retailPrice)}</div>
        <RetailerIconGroup
          retailerIds={retailerIds}
          iconSize={retailerLogoWidth}
          maxSize={5}
        />
      </div>
      {isServerSideCheckoutEligible(checkoutEligibility) && (
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          {isInCart ? (
            <OutlineButton
              fullWidth
              onClick={openDrawer}
              sx={{
                fontSize: mobileView ? 12 : 14,
                height: mobileView ? 36 : 40,
                borderColor: "#6b7280",
                color: "#6b7280",
                "&:hover": {
                  borderColor: "#4b5563",
                  backgroundColor: "#f9fafb",
                },
              }}
            >
              <CheckCircleIcon size={16} style={{ marginRight: 8 }} />
              Added to Bag
            </OutlineButton>
          ) : (
            <SolidButton
              fullWidth
              onClick={handleAddToBag}
              sx={{
                fontSize: mobileView ? 12 : 14,
                height: mobileView ? 36 : 40,
              }}
            >
              Add to Bag
            </SolidButton>
          )}
        </div>
      )}
      <RewardPillWithText
        rewardAmount={purchaseRewardAmount ?? 0}
        rewardCampaignType={
          hasActiveInstantReward
            ? CampaignType.DailyDealsRewardCampaign
            : CampaignType.PurchaseRewardCampaign
        }
        rewardPercent={rewardPercent}
        rewardPercentText="reward"
        containerStyle={mobileView ? { columnGap: 8 } : undefined}
        baseFontSize={mobileView ? 11 : 14}
      />
    </ProductCard>
  );
};
